import { useEffect, useState, type CSSProperties } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useStore } from '../store';
import { showToast } from './Toast';
import { FREE_LIMITS, isPro, showProNotice, useIsPro } from '../entitlements';
import {
  createCloudProject,
  deleteCloudProject,
  listCloudProjects,
  loadCloudProject,
  renameCloudProject,
  type CloudProjectRow,
} from '../cloudProjects';

/**
 * "My projects" browser — lists the signed-in user's cloud-saved projects and
 * lets them open / create / rename / delete. Reads and writes the live document
 * through the store directly; the only thing it hands back to <App> is which
 * cloud project is now active (so App's cloud-autosave targets the right row).
 */
export function ProjectsModal({
  onClose,
  currentCloudProjectId,
  onActiveProjectChange,
}: {
  onClose: () => void;
  currentCloudProjectId: string | null;
  onActiveProjectChange: (id: string | null) => void;
}) {
  const [rows, setRows] = useState<CloudProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const pro = useIsPro();
  const atFreeLimit = !pro && rows.length >= FREE_LIMITS.cloudProjects;

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listCloudProjects());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your projects.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    setNewName(useStore.getState().meta.name || 'Untitled project');
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const saveCurrentAsNew = async () => {
    if (!isPro() && rows.length >= FREE_LIMITS.cloudProjects) {
      showProNotice(
        `Free plan is limited to ${FREE_LIMITS.cloudProjects} saved project. Upgrade to Pro for unlimited cloud projects.`,
      );
      return;
    }
    const name = newName.trim() || 'Untitled project';
    setBusy(true);
    try {
      const s = useStore.getState();
      const id = await createCloudProject(name, {
        meta: s.meta,
        sheets: s.sheets,
        activeSheetId: s.activeSheetId,
      });
      onActiveProjectChange(id);
      showToast(`Saved “${name}” to your account`, 'success');
      await refresh();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Save failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const open = async (id: string) => {
    setBusy(true);
    try {
      const data = await loadCloudProject(id);
      useStore.getState().setProject({
        meta: data.meta,
        sheets: data.sheets,
        activeSheetId: data.activeSheetId,
      });
      onActiveProjectChange(id);
      showToast('Project opened', 'info');
      onClose();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Could not open project', 'error');
      setBusy(false);
    }
  };

  const commitRename = async (id: string) => {
    const name = renameValue.trim();
    if (!name) {
      setRenamingId(null);
      return;
    }
    setBusy(true);
    try {
      await renameCloudProject(id, name);
      setRenamingId(null);
      await refresh();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Rename failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: CloudProjectRow) => {
    if (!window.confirm(`Delete “${row.name}”? This can't be undone.`)) return;
    setBusy(true);
    try {
      await deleteCloudProject(row.id);
      if (row.id === currentCloudProjectId) onActiveProjectChange(null);
      await refresh();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlay} onMouseDown={onClose}>
      <div style={card} onMouseDown={(e) => e.stopPropagation()}>
        <div style={headerRow}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>My projects</div>
          <button style={iconBtn} onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        <div style={saveRow}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Project name"
            style={input}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !busy) void saveCurrentAsNew();
            }}
          />
          <button
            style={{ ...primaryBtn, opacity: busy || atFreeLimit ? 0.55 : 1 }}
            disabled={busy || atFreeLimit}
            onClick={() => void saveCurrentAsNew()}
          >
            <Plus size={14} /> Save current
          </button>
        </div>
        <div style={hint}>
          {atFreeLimit
            ? `Free plan: ${FREE_LIMITS.cloudProjects} saved project. Upgrade to Pro for unlimited.`
            : 'Saves whatever is on your canvas now as a new named project.'}
        </div>

        <div style={sep} />

        {loading ? (
          <div style={muted}>Loading…</div>
        ) : error ? (
          <div style={errorBox}>{error}</div>
        ) : rows.length === 0 ? (
          <div style={muted}>
            No saved projects yet. Use “Save current” above to store your first one.
          </div>
        ) : (
          <div style={list}>
            {rows.map((row) => {
              const isOpen = row.id === currentCloudProjectId;
              const isRenaming = row.id === renamingId;
              return (
                <div key={row.id} style={{ ...rowStyle, ...(isOpen ? rowOpen : {}) }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    {isRenaming ? (
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void commitRename(row.id);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        style={{ ...input, padding: '5px 8px' }}
                      />
                    ) : (
                      <>
                        <div style={nameText}>
                          {row.name}
                          {isOpen && <span style={openTag}>open</span>}
                        </div>
                        <div style={dateText}>Updated {formatWhen(row.updatedAt)}</div>
                      </>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {isRenaming ? (
                      <button style={iconBtn} title="Save name" onClick={() => void commitRename(row.id)}>
                        <Check size={15} />
                      </button>
                    ) : (
                      <>
                        <button style={openBtn} disabled={busy} onClick={() => void open(row.id)}>
                          Open
                        </button>
                        <button
                          style={iconBtn}
                          title="Rename"
                          onClick={() => {
                            setRenamingId(row.id);
                            setRenameValue(row.name);
                          }}
                        >
                          <Pencil size={14} />
                        </button>
                        <button style={iconBtnDanger} title="Delete" onClick={() => void remove(row)}>
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function formatWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  return new Date(iso).toLocaleDateString();
}

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};
const card: CSSProperties = {
  background: 'var(--panel)',
  color: 'var(--text)',
  border: '1px solid var(--panel-border-strong, var(--panel-border))',
  borderTop: '2px solid var(--accent)',
  borderRadius: 'var(--radius)',
  padding: 18,
  width: 460,
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 80px)',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: 'var(--shadow-lg)',
};
const headerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12,
};
const saveRow: CSSProperties = { display: 'flex', gap: 8 };
const input: CSSProperties = {
  flex: 1,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel-alt, var(--panel))',
  color: 'var(--text)',
  padding: '8px 10px',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  minWidth: 0,
};
const primaryBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--accent)',
  color: '#fff',
  fontSize: 12.5,
  fontWeight: 700,
  padding: '8px 12px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const hint: CSSProperties = { fontSize: 11.5, color: 'var(--text-muted)', margin: '6px 2px 0' };
const sep: CSSProperties = { height: 1, background: 'var(--panel-border)', margin: '14px 0' };
const list: CSSProperties = { overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 };
const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--panel-alt, var(--panel))',
};
const rowOpen: CSSProperties = { borderColor: 'var(--accent)', boxShadow: '0 0 0 1px var(--accent) inset' };
const nameText: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const openTag: CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--accent)',
  border: '1px solid var(--accent)',
  borderRadius: 10,
  padding: '0 6px',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};
const dateText: CSSProperties = { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 };
const openBtn: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--text)',
  fontSize: 12,
  fontWeight: 700,
  padding: '5px 12px',
  cursor: 'pointer',
};
const iconBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--text-muted)',
  cursor: 'pointer',
};
const iconBtnDanger: CSSProperties = { ...iconBtn, color: 'var(--danger, #dc2626)' };
const muted: CSSProperties = { fontSize: 12.5, color: 'var(--text-muted)', padding: '8px 2px', lineHeight: 1.5 };
const errorBox: CSSProperties = {
  fontSize: 12.5,
  color: '#fecaca',
  background: 'rgba(220,38,38,0.12)',
  border: '1px solid rgba(220,38,38,0.4)',
  borderRadius: 'var(--radius-sm)',
  padding: '8px 10px',
  lineHeight: 1.4,
};
